import { useRef, useEffect, useState } from 'react';
import mapboxgl from 'mapbox-gl';
import { Box, IconButton } from '@mui/material';
import { BiCog, BiLayer } from 'react-icons/bi';

const convertToGeojson = (from) => {
    const geojsonFeatures = from.map(project => ({
        type: "Feature",
        geometry: {
          type: "Point",
          coordinates: [project.longitude, project.latitude] // GeoJSON uses [lng, lat]
        },
        properties: {
          id: project.id,
          project_name: project.project_name,
          project_country: project.project_country,
          safet_theme: project.safet_theme,
          technology_types: project.technology_types,
          project_stage: project.project_stage,
          project_impact: project.project_impact,
          project_description: project.project_description
        }
    }));
      
    // Wrap in a GeoJSON FeatureCollection
    const geojson = {
        type: "FeatureCollection",
        features: geojsonFeatures
    };

    return geojson;
}

const theStyles = [
'mapbox://styles/safet-fish/cmb9v575b00us01s6ctg4ewb3',
'mapbox://styles/mapbox/satellite-v9'
];

const Map = (props) => {
    const mapRef = useRef();
    const mapContainerRef = useRef();
    const [ mapInitialised, setMapInitialised ] = useState(false);
    const [ currentStyle, setCurrentStyle ] = useState(0);
    const [ maximumHeight, setMaximumHeight ] = useState(600);

    const toggleStyle = () => {
        let newStyle = currentStyle === 0 ? 1 : 0;
        setCurrentStyle(newStyle);
        setMapInitialised(false);

        mapRef.current.on('style.load', () => {
            setMapInitialised(true);
        });

        mapRef.current.setStyle(theStyles[newStyle]);
    };

    useEffect(() => {
        // The containing window can resize all it likes, but if it gets bigger than now, don't.
        // This is mostly to manage being in an iframe. This will cause problems if the user
        // starts with a small window and maximises it, but prevents oversize problems in
        // otherwise normal operation.
        setMaximumHeight(window.innerHeight / 2);

        mapboxgl.accessToken = 'pk.eyJ1Ijoic2FmZXQtZmlzaCIsImEiOiJjbWI4aDdlM3IwN3BoMmpzYjB4NnJwZXUyIn0.P0Ty8uA6pxsw8xzrD-AbXA'
        mapRef.current = new mapboxgl.Map({
            container: mapContainerRef.current,
            style: theStyles[currentStyle],
            center: [0, 0],
            zoom: 1.45,
            cooperativeGestures: true
        });

        mapRef.current.on('load', () => {

            setMapInitialised(true);
        });

        return () => {
            mapRef.current.remove();
        }
    }, []);

    useEffect(() => {
        if (mapInitialised && props.projects && props.projects.length > 0) {

            const geojson = convertToGeojson(props.projects);

            mapRef.current.addSource('projects', {
                type: 'geojson',
                data: geojson,
                cluster: true,
                clusterMaxZoom: 14,
                clusterRadius: 20
            });

            mapRef.current.addLayer({
                id: 'clusters',
                type: 'circle',
                source: 'projects',
                filter: ['has', 'point_count'],
                paint: {
                    'circle-color': "#ffad00",  // was #ffdc2e
                    'circle-radius': 20
                }
            });

            mapRef.current.addLayer({
                id: 'cluster-count',
                type: 'symbol',
                source: 'projects',
                filter: ['has', 'point_count'],
                layout: {
                    'text-field': ['get', 'point_count_abbreviated'],
                    'text-font': ['DIN Offc Pro Medium', 'Arial Unicode MS Bold'],
                    'text-size': 12
                }
            });

            mapRef.current.addLayer({
                id: 'unclustered-point',
                type: 'circle',
                source: 'projects',
                filter: ['!', ['has', 'point_count']],
                paint: {
                    'circle-color': '#ffad00',
                    'circle-radius': 5,
                    'circle-stroke-width': 1,
                    'circle-stroke-color': '#ffad00'
                }
            });

            mapRef.current.on('click', 'clusters', (e) => {
                const features = mapRef.current.queryRenderedFeatures(e.point, {
                    layers: ['clusters']
                });
                const clusterId = features[0].properties.cluster_id;
                mapRef.current
                    .getSource('projects')
                    .getClusterExpansionZoom(clusterId, (err, zoom) => {
                        if (err) return;

                        mapRef.current.easeTo({
                            center: features[0].geometry.coordinates,
                            zoom: zoom
                        });
                });
            });

            mapRef.current.on('click', 'unclustered-point', (e) => {
                const coordinates = e.features[0].geometry.coordinates.slice();
                const id = e.features[0].properties.id;
                const zm = mapRef.current.getZoom();

                while (Math.abs(e.lngLat.lng - coordinates[0]) > 180) {
                    coordinates[0] += e.lngLat.lng > coordinates[0] ? 360 : -360;
                }

                // new mapboxgl.Popup()
                //     .setLngLat(coordinates)
                //     .setHTML(`project: ${id}`)
                //     .addTo(mapRef.current);
                console.log('Clicked on project ' + id + ' at ' + zm);
                let pro = props.projects.find( p => p.id === id);
                if (pro) {
                    props.onSelectResult(pro);
                }
            });

            mapRef.current.on('mouseenter', 'clusters', () => {
                mapRef.current.getCanvas().style.cursor = 'pointer';
            });
            mapRef.current.on('mouseleave', 'clusters', () => {
                mapRef.current.getCanvas().style.cursor = '';
            });

            mapRef.current.on('mouseenter', 'unclustered-point', () => {
                mapRef.current.getCanvas().style.cursor = 'pointer';
            });
            mapRef.current.on('mouseleave', 'unclustered-point', () => {
                mapRef.current.getCanvas().style.cursor = '';
            });
        }
    }, [mapInitialised, props.projects]);
  
    return(
    <Box sx={{position: 'relative', height: '50vh', maxHeight: maximumHeight}}>
        <div id='map-container' ref={mapContainerRef} />
        <IconButton onClick={() => toggleStyle()} sx={{position: 'absolute', top: '10px', left: '10px', zIndex: '1'}}>
            <BiLayer size="1.0em" />
        </IconButton>
    </Box>
    );
}
 
export default Map;